import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography
} from '@mui/material';
import {
  Home,
  Engineering,
  VerifiedUser,
  CheckCircle,
  HomeWork,
  Gavel
} from '@mui/icons-material';
import { brand } from '../../theme/colors';
import { useFeatures } from '../../contexts/FeaturesContext';

const ROLE_OPTIONS = [
  {
    value: 'client',
    title: 'I Need Work Done',
    subtitle: 'Property Owner',
    description: 'Hire verified contractors, track progress, and pay only for completed work',
    icon: Home,
    color: brand.primary
  },
  {
    value: 'contractor',
    title: 'I Do The Work',
    subtitle: 'Apply to join as a verified contractor',
    description: 'Find construction jobs, submit AI-powered proposals, and get paid securely for completed work',
    icon: Engineering,
    color: brand.secondary
  },
  {
    value: 'verifier',
    title: 'I Verify Quality',
    subtitle: 'Apply to verify work as an independent inspector',
    description: 'Inspect construction milestones on-site, submit photo evidence and AI-powered reports, and get paid for every completed verification',
    icon: VerifiedUser,
    color: brand.accent
  },
  {
    value: 'realtor',
    title: 'I Sell Property',
    subtitle: 'Apply to list stands and houses as an EAC-registered estate agent',
    description: 'List verified stands and houses, complete due diligence with Eqaya, and reach diaspora buyers who can trust what they see',
    icon: HomeWork,
    color: brand.primary,
    // Estate agents only exist while the property-listings vertical is on.
    feature: 'listings'
  },
  {
    value: 'conveyancer',
    title: 'I Transfer Property',
    subtitle: 'Apply as a conveyancer listed by the Law Society of Zimbabwe',
    description: 'Be found by buyers of verified homes, at home and abroad, with a good-standing badge checked against the Law Society register',
    icon: Gavel,
    color: brand.accent,
    feature: 'listings'
  }
];

const ImprovedRoleSelector = ({ selectedRole, onRoleSelect }) => {
  // A role behind a switched-off feature is not offered at all — otherwise a
  // visitor fills in the whole form and is refused at the last step (prod,
  // 2026-09-26: "I Sell Property" showed while listings were off).
  const features = useFeatures();
  const options = ROLE_OPTIONS.filter((role) => !role.feature || features[role.feature]);
  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
          What brings you to Eqaya?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Pick the option that describes you best
        </Typography>
      </Box>

      {/* Role cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {options.map((role) => {
          const IconComponent = role.icon;
          const isSelected = selectedRole === role.value;

          return (
            <Card
              key={role.value}
              onClick={() => onRoleSelect(role.value)}
              sx={{
                cursor: 'pointer',
                border: '2px solid',
                borderColor: isSelected ? role.color : '#e2e8f0',
                bgcolor: isSelected ? `${role.color}08` : 'background.paper',
                transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: role.color,
                  bgcolor: `${role.color}06`
                }
              }}
              elevation={isSelected ? 3 : 1}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                {/* Icon */}
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: isSelected ? role.color : '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <IconComponent sx={{ fontSize: 24, color: isSelected ? 'white' : '#64748b' }} />
                </Box>

                {/* Text */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                    {role.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: role.color, fontWeight: 600 }}>
                    {role.subtitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {role.description}
                  </Typography>
                </Box>

                {/* Selected indicator */}
                {isSelected && (
                  <CheckCircle sx={{ fontSize: 24, color: role.color }} />
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default ImprovedRoleSelector;
